import React, { useState, useEffect } from "react";
import {
  Badge,
  IconButton,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
  CircularProgress,
  Paper,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../contexts/CartContext";
import { formatCurrency } from "../../utils/formatters";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getDefaultTaxRate } from "../../store/slices/taxSlice";

/**
 * MiniCart component for displaying cart in header
 * @returns {JSX.Element} MiniCart component
 */
const MiniCart = () => {
  const { t } = useTranslation("common");
  const { t: tCart } = useTranslation("cart");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const { cart, loading, totalItems, removeFromCart } = useCart();
  const { taxRate } = useSelector((state) => state.tax);

  // Fetch tax rate when component mounts
  useEffect(() => {
    dispatch(getDefaultTaxRate());
  }, [dispatch]);

  // Handle open cart
  const handleOpenCart = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle close cart
  const handleCloseCart = () => {
    setAnchorEl(null);
  };

  // Handle view cart
  const handleViewCart = () => {
    handleCloseCart();
    navigate("/cart");
  };

  // Handle remove item
  const handleRemoveItem = (event, itemId) => {
    event.stopPropagation();
    removeFromCart(itemId);
  };

  const open = Boolean(anchorEl);
  const id = open ? "mini-cart-popover" : undefined;

  return (
    <>
      <IconButton
        color="inherit"
        aria-label="cart"
        onClick={handleOpenCart}
        aria-describedby={id}
      >
        <Badge badgeContent={totalItems} color="error">
          <ShoppingCartIcon />
        </Badge>
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseCart}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            width: 360,
            boxShadow: "0px 5px 15px rgba(0,0,0,0.2)",
            maxHeight: "none",
            overflow: "visible",
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ p: 3, pb: 2 }}>
            <Typography variant="h6" gutterBottom>
              {tCart("miniCart.title")}
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !cart || !cart.items || cart.items.length === 0 ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {tCart("miniCart.emptyState")}
              </Typography>
            </Box>
          ) : (
            <>
              <List
                sx={{
                  width: "100%",
                  maxHeight: 280,
                  overflow: "auto",
                  px: 3,
                }}
              >
                {cart.items
                  .slice()
                  .sort((a, b) => {
                    // Sort by product name first
                    const nameA = a.product?.name?.toLowerCase() || "";
                    const nameB = b.product?.name?.toLowerCase() || "";

                    // First by name
                    if (nameA < nameB) return -1;
                    if (nameA > nameB) return 1;

                    // If names are the same, sort by variation name if available
                    if (a.product?.variation && b.product?.variation) {
                      const varNameA = a.product.variation.name.toLowerCase();
                      const varNameB = b.product.variation.name.toLowerCase();
                      return varNameA.localeCompare(varNameB);
                    }

                    return 0;
                  })
                  .map((item) => (
                    <React.Fragment key={item.id}>
                      <ListItem
                        alignItems="flex-start"
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label={tCart("page.productDetails.removeItem")}
                            onClick={(e) => handleRemoveItem(e, item.id)}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                        sx={{
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                          py: 1.5,
                          px: 0,
                        }}
                        onClick={() => {
                          handleCloseCart();
                          navigate(
                            `/products/${
                              item.product.parentId || item.product.id
                            }`
                          );
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            alt={item.product?.name}
                            src={
                              item.product?.imageUrl ||
                              item.product?.parentProduct?.imageUrl ||
                              item.product?.parent?.imageUrl ||
                              "/placeholder.svg"
                            }
                            variant="square"
                            sx={{ width: 40, height: 40 }}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <>
                              {item.product?.name}
                              {item.product?.variation && (
                                <>
                                  {" - "}
                                  {item.product.variation.name.startsWith(
                                    item.product.name
                                  )
                                    ? item.product.variation.name
                                        .substring(item.product.name.length)
                                        .trim()
                                        .replace(/^-\s*/, "")
                                    : item.product.variation.name}
                                </>
                              )}
                            </>
                          }
                          secondary={
                            <>
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.primary"
                              >
                                {formatCurrency(item.price)} × {item.quantity}
                              </Typography>
                              {" — "}
                              {formatCurrency(item.price * item.quantity)}
                            </>
                          }
                        />
                      </ListItem>
                      <Divider variant="inset" component="li" />
                    </React.Fragment>
                  ))}
              </List>

              <Box sx={{ px: 3, pt: 2, pb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2">
                    {tCart("orderSummary.subtotal")}:
                  </Typography>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {formatCurrency(cart.subtotal)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2">
                    {tCart("orderSummary.taxWithRate", {
                      rate: cart.taxRate
                        ? (cart.taxRate * 100).toFixed(1)
                        : taxRate?.rate
                        ? (taxRate.rate * 100).toFixed(1)
                        : 7.7,
                    })}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {formatCurrency(cart.tax)}
                  </Typography>
                </Box>

                {cart.discount > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                      color: "success.main",
                    }}
                  >
                    <Typography variant="subtitle2">
                      {cart.couponCode
                        ? tCart("orderSummary.discountWithCode", {
                            code: cart.couponCode,
                          })
                        : tCart("orderSummary.discount")}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight="bold">
                      -{formatCurrency(cart.discount)}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 1.5 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1">
                    {tCart("orderSummary.total")}:
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {formatCurrency(cart.total)}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleViewCart}
                  sx={{ py: 1 }}
                >
                  {tCart("miniCart.viewCart")}
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Popover>
    </>
  );
};

export default MiniCart;
